import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { allTopics } from '@/utils/data';

const navLinks = [
  { label: '影视', to: '/category/film' },
  { label: '音乐', to: '/category/music' },
  { label: '明星', to: '/category/star' },
  { label: '潮流', to: '/category/trend' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setTopicOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setKeyword('');
  };

  const linkBase = transparent
    ? 'text-background-50 hover:text-primary-200'
    : 'text-foreground-700 hover:text-primary-600';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        transparent ? 'bg-transparent' : 'bg-background-50/95 backdrop-blur border-b border-background-200'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-play-circle-fill text-xl"></i>
            </span>
            <span className={`font-brand text-2xl leading-none ${transparent ? 'text-background-50' : 'text-primary-600'}`}>
              色哟哟
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-md px-3.5 py-2 text-sm font-medium transition-colors cursor-pointer ${linkBase}`}
              >
                {link.label}
              </Link>
            ))}
            <div
              className="relative"
              onMouseEnter={() => setTopicOpen(true)}
              onMouseLeave={() => setTopicOpen(false)}
            >
              <button
                type="button"
                className={`flex items-center gap-1 rounded-md px-3.5 py-2 text-sm font-medium transition-colors cursor-pointer ${linkBase}`}
              >
                专题
                <i className={`ri-arrow-down-s-line transition-transform ${topicOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {topicOpen && (
                <div className="absolute left-0 top-full pt-2">
                  <div className="w-72 rounded-lg border border-background-200 bg-background-50 p-2 shadow-sm">
                    {allTopics.map((t) => (
                      <Link
                        key={t.id}
                        to={`/category/${t.slug}`}
                        className="flex items-start gap-3 rounded-md p-2.5 cursor-pointer transition-colors hover:bg-primary-50"
                      >
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                          <i className="ri-bookmark-line"></i>
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-foreground-800">{t.title}</span>
                          <span className="mt-0.5 block text-xs text-foreground-500 line-clamp-1">{t.desc}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/about"
              className={`rounded-md px-3.5 py-2 text-sm font-medium transition-colors cursor-pointer ${linkBase}`}
            >
              关于
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center">
              {searchOpen ? (
                <form onSubmit={submitSearch} className="flex items-center">
                  <input
                    ref={searchRef}
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onBlur={() => !keyword && setSearchOpen(false)}
                    placeholder="搜索娱乐资讯"
                    className="w-48 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
                  />
                  <button type="submit" className="ml-1.5 rounded-md p-2 cursor-pointer text-foreground-600 hover:text-primary-600">
                    <i className="ri-search-line text-lg"></i>
                  </button>
                </form>
              ) : (
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  aria-label="搜索"
                  className={`rounded-md p-2 cursor-pointer transition-colors ${transparent ? 'text-background-50 hover:text-primary-200' : 'text-foreground-600 hover:text-primary-600'}`}
                >
                  <i className="ri-search-line text-lg"></i>
                </button>
              )}
            </div>

            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className={`md:hidden rounded-md p-2 cursor-pointer transition-colors ${transparent ? 'text-background-50' : 'text-foreground-700'}`}
            >
              <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 animate-fade-rise">
          <form onSubmit={submitSearch} className="mb-3 flex items-center gap-2">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索娱乐资讯"
              className="flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none focus:border-primary-400"
            />
            <button type="submit" className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 cursor-pointer whitespace-nowrap">
              搜索
            </button>
          </form>
          <div className="flex flex-col">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md px-3 py-3 text-base font-medium text-foreground-800 cursor-pointer hover:bg-primary-50"
              >
                {link.label}
              </Link>
            ))}
            {allTopics.map((t) => (
              <Link
                key={t.id}
                to={`/category/${t.slug}`}
                className="rounded-md px-3 py-3 text-base font-medium text-foreground-800 cursor-pointer hover:bg-primary-50"
              >
                专题 · {t.title}
              </Link>
            ))}
            <Link
              to="/about"
              className="rounded-md px-3 py-3 text-base font-medium text-foreground-800 cursor-pointer hover:bg-primary-50"
            >
              关于
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}